/* Created by Interactive 63 - https://interactive63.com */
/**
 * Editor actions for gradient colour stops: deleting, adding one between two others, and moving
 * one along its gradient. Each is one undo step and leaves the selection on something that exists.
 */
import type { ElementId } from '../../core/model/types';
import { addStopAfter, deleteStops, setStopOffset } from '../../core/properties/stops';
import { selectElements, clearSelection } from './actions';
import type { ArrangeOutcome } from './layer-actions';
import type { Store } from './store';

/** The selected elements that are gradient stops. */
export function selectedStops(store: Store): ElementId[] {
  const { project, editor } = store.getState();
  return editor.selection.elementIds.filter((id) => project.elements[id]?.tag === 'stop');
}

/** Deletes the selected stops, keeping every gradient at two or more. */
export function deleteSelectedStops(store: Store): ArrangeOutcome {
  const stops = selectedStops(store);
  if (stops.length === 0) return { changed: false };
  let removed: ElementId[] = [];
  let reason: string | undefined;
  store.updateProject('Delete colour stop', (draft) => {
    const result = deleteStops(draft, stops);
    removed = result.removed;
    reason = result.reason;
  });
  if (removed.length > 0) clearSelection(store);
  return { changed: removed.length > 0, ...(reason ? { reason } : {}) };
}

/** Adds a stop after the selected one and selects it. */
export function addStopAfterSelected(store: Store): ArrangeOutcome {
  const [first] = selectedStops(store);
  if (first === undefined) return { changed: false };
  let added: ElementId = '';
  store.updateProject('Add colour stop', (draft) => {
    added = addStopAfter(draft, first) ?? '';
  });
  if (added === '') return { changed: false };
  selectElements(store, [added]);
  return { changed: true };
}

/** Moves a stop along its gradient, as a percentage. */
export function moveStop(store: Store, id: ElementId, percent: number, coalesceKey?: string): void {
  store.updateProject(
    'Move colour stop',
    (draft) => {
      setStopOffset(draft, id, percent);
    },
    coalesceKey ? { coalesceKey } : undefined,
  );
}
